import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    ThemeProvider,
    createTheme,
    CssBaseline,
    GlobalStyles,
    alpha,
} from '@mui/material';
import { Mode, palettes, accent, gradients, fonts, cssVars } from './tokens';

interface ThemeModeCtx {
    mode: Mode;
    toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeCtx>({
    mode: 'light',
    toggle: () => { },
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [mode, setMode] = useState<Mode>(
        () => (localStorage.getItem('themeMode') as Mode) || 'light'
    );

    useEffect(() => {
        localStorage.setItem('themeMode', mode);
        document.documentElement.setAttribute('data-theme', mode);
    }, [mode]);

    const toggle = () => setMode(m => (m === 'light' ? 'dark' : 'light'));

    const theme = useMemo(() => {
        const p = palettes[mode];
        const isDark = mode === 'dark';

        return createTheme({
            palette: {
                mode,
                primary: { main: accent.indigo, light: '#6ea8ff', dark: '#1e5fd8', contrastText: '#fff' },
                secondary: { main: accent.cyan, contrastText: '#06212a' },
                error: { main: accent.rose },
                warning: { main: accent.amber },
                success: { main: accent.emerald },
                divider: p.border,
                background: { default: p.bg, paper: p.surface },
                text: { primary: p.text, secondary: p.textMuted },
            },
            shape: { borderRadius: 12 },
            typography: {
                fontFamily: fonts.body,
                h1: { fontFamily: fonts.display, fontWeight: 700, letterSpacing: '-0.02em' },
                h2: { fontFamily: fonts.display, fontWeight: 700, letterSpacing: '-0.02em' },
                h3: { fontFamily: fonts.display, fontWeight: 700, letterSpacing: '-0.015em' },
                h4: { fontFamily: fonts.display, fontWeight: 700, letterSpacing: '-0.01em' },
                h5: { fontFamily: fonts.display, fontWeight: 600 },
                h6: { fontFamily: fonts.display, fontWeight: 600 },
                button: { fontWeight: 600, letterSpacing: '0.01em' },
            },
            components: {
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            border: `1px solid ${p.border}`,
                            boxShadow: p.shadow,
                        },
                        elevation0: { boxShadow: 'none' },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            borderRadius: 16,
                            border: `1px solid ${p.border}`,
                            backgroundImage: 'none',
                            boxShadow: p.shadow,
                            transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
                        },
                    },
                },
                MuiButton: {
                    defaultProps: { disableElevation: true },
                    styleOverrides: {
                        root: { borderRadius: 10, textTransform: 'none', fontWeight: 600, paddingInline: 18 },
                        containedPrimary: {
                            background: gradients.brandSoft,
                            boxShadow: p.glow,
                            '&:hover': { background: gradients.brand, boxShadow: p.glow },
                        },
                        outlined: { borderColor: p.borderStrong },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 10,
                            backgroundColor: isDark ? alpha('#ffffff', 0.03) : alpha('#0b0e16', 0.015),
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: p.border },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: p.borderStrong },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: accent.indigo,
                                boxShadow: `0 0 0 3px ${alpha(accent.indigo, 0.18)}`,
                            },
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: { root: { borderRadius: 8, fontWeight: 600 } },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            background: isDark ? '#222742' : '#1a1c2e',
                            fontSize: 12,
                            fontWeight: 500,
                            borderRadius: 8,
                            padding: '6px 10px',
                        },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        head: { fontWeight: 700, color: p.textMuted, borderColor: p.border },
                        root: { borderColor: p.border },
                    },
                },
            },
        });
    }, [mode]);

    return (
        <ThemeModeContext.Provider value={{ mode, toggle }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <GlobalStyles
                    styles={{
                        ':root': cssVars(mode),
                        'html, body, #root': { height: '100%' },
                        body: {
                            fontFamily: fonts.body,
                            background: palettes[mode].bgGradient,
                            backgroundAttachment: 'fixed',
                            color: palettes[mode].text,
                            transition: 'background-color .35s ease, color .35s ease',
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                        },
                        'input, select, textarea': { borderRadius: '10px', fontFamily: fonts.body },
                        /* scrollbar global discreta e moderna */
                        '*::-webkit-scrollbar': { width: 10, height: 10 },
                        '*::-webkit-scrollbar-track': { background: 'transparent' },
                        '*::-webkit-scrollbar-thumb': {
                            background: palettes[mode].scrollThumb,
                            borderRadius: 8,
                            border: '2px solid transparent',
                            backgroundClip: 'content-box',
                        },
                        '*::-webkit-scrollbar-thumb:hover': { background: accent.indigo },
                        '::selection': { background: alpha(accent.indigo, 0.3) },
                    }}
                />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
};
